import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { pick, asList, fmtDateTime } from "@/lib/admin-utils";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { isTerminalStatus } from "@/lib/status";

export function AdminRequests() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-requests"],
    queryFn: async () => (await api.get("/admin/requests")).data,
  });

  const requests = asList(data);

  const cancel = async (r) => {
    setBusy(true);
    try {
      await api.post(`/requests/${r.id}/cancel`);
      toast.success("Richiesta annullata");
      qc.invalidateQueries({ queryKey: ["admin-requests"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 pt-5 space-y-4" data-testid="admin-requests-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Richieste</h1>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
      ) : requests.length ? (
        <div className="space-y-3" data-testid="requests-list">
          {requests.map((r) => (
            <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{pick(r, ["title", "raw_text"], "Richiesta")}</p>
                  <p className="text-xs text-slate-500 capitalize">
                    {pick(r, ["category"], "—")} · Cliente: {pick(r, ["client_name", "client_email", "user_id"], "—")} · {pick(r, ["zone", "address"], "—")}
                  </p>
                  <p className="text-xs text-slate-400">
                    {fmtDateTime(pick(r, ["created_at"]))}
                    {r.professional_id || r.pro_id ? ` · Pro: ${r.professional_id || r.pro_id}` : ""}
                  </p>
                </div>
                <StatusBadge status={r.status} testid={`admin-request-status-${r.id}`} />
              </div>
              {!isTerminalStatus(r.status) && (
                <Button data-testid={`moderate-request-${r.id}`} size="sm" variant="outline" disabled={busy} onClick={() => cancel(r)}
                  className="rounded-full text-red-600 border-red-200 font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-1" /> Annulla richiesta</>}
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="requests-empty">Nessuna richiesta presente.</p>
      )}
    </div>
  );
}
